import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { motion, AnimatePresence } from "framer-motion";

import { Input } from "@/components/ui";
import useDebounce from "@/hooks/useDebounce";
import { GridPostList } from "@/components/shared";
import { useGetPosts, useSearchPosts } from "@/lib/react-query/queries";
import SEOComponent from "@/components/shared/SEOComponent";

export type SearchResultProps = {
  isSearchFetching: boolean;
  searchedPosts: {
    documents: any[];
    total: number;
  };
};

const SearchResults = ({ isSearchFetching, searchedPosts }: SearchResultProps) => {
  if (isSearchFetching) {
    return (
      <div className="flex-center w-full min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-primary-500/30 rounded-full animate-spin border-t-primary-500"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent rounded-full animate-ping border-t-primary-500/20"></div>
          </div>
          <p className="text-light-3 animate-pulse">Searching posts...</p>
        </div>
      </div>
    );
  } else if (searchedPosts.documents.length > 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full"
      >
        <motion.div 
          className="flex items-center justify-center gap-3 mb-8 p-4 bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-xl border border-primary-500/20"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="w-6 h-6 bg-gradient-to-r from-primary-500 to-purple-500 rounded-full flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-light-1 font-semibold">
            Found <span className="text-primary-500">{searchedPosts.total}</span> result{searchedPosts.total !== 1 ? 's' : ''}
          </p>
        </motion.div>
        <GridPostList posts={searchedPosts.documents} />
      </motion.div>
    );
  } else {
    return (
      <motion.div 
        className="flex flex-col items-center justify-center min-h-[400px] w-full text-center"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="w-24 h-24 bg-dark-4 rounded-full flex items-center justify-center mb-6">
          <svg className="w-12 h-12 text-light-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-light-1 mb-2">No Results Found</h3>
        <p className="text-light-3 max-w-md">
          We couldn't find any posts matching your search. Try different keywords or check your spelling.
        </p>
      </motion.div>
    );
  }
};

const Explore = () => {
  const { ref, inView } = useInView();
  const { data: posts, fetchNextPage, hasNextPage } = useGetPosts();

  const [searchValue, setSearchValue] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const debouncedSearch = useDebounce(searchValue, 500);
  
  const { 
    data: searchedPosts, 
    isFetching: isSearchFetching 
  } = useSearchPosts(debouncedSearch);

  useEffect(() => {
    if (inView && !searchValue) {
      fetchNextPage();
    }
  }, [inView, searchValue, fetchNextPage]);

  if (!posts)
    return (
      <div className="flex-center w-full h-full min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-primary-500/30 rounded-full animate-spin border-t-primary-500"></div>
            <div className="absolute inset-0 w-20 h-20 border-4 border-transparent rounded-full animate-ping border-t-primary-500/20"></div>
          </div>
          <p className="text-light-3 text-lg">Loading posts...</p>
        </div>
      </div>
    );

  const shouldShowSearchResults = searchValue !== "";
  const shouldShowPosts = !shouldShowSearchResults && 
    posts.pages.every((item) => item.documents.length === 0);

  return (
    <>
      <SEOComponent 
        title="Explore Products & Services | Busiconn"
        description="Explore offers from sellers and freelancers on Busiconn. Find products and services, start a conversation, and agree on your next deal."
        canonicalUrl="https://busiconn.com/explore"
      />
      <motion.div 
        className="explore-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="explore-inner_container">
          {/* Enhanced Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col gap-6 mb-8"
          >
            <div className="text-center">
              <h2 className="h3-bold md:h2-bold bg-gradient-to-r from-primary-500 to-purple-500 bg-clip-text text-transparent mb-2">
                Discover & Search Posts
              </h2>
              <p className="text-light-3 max-w-2xl mx-auto">
                Find exactly what you're looking for from our community of businesses, services, and products.
              </p>
            </div>

            {/* Enhanced Search Input */}
            <motion.div 
              className={`relative transition-all duration-300 ${
                isSearchFocused ? 'scale-105' : 'scale-100'
              }`}
              whileHover={{ scale: 1.02 }}
            >
              <div className={`flex items-center gap-3 px-6 py-4 w-full rounded-2xl transition-all duration-300 ${
                isSearchFocused 
                  ? 'bg-gradient-to-r from-dark-3 to-dark-4 border-2 border-primary-500/50 shadow-lg' 
                  : 'bg-dark-4 border-2 border-transparent hover:border-primary-500/30'
              }`}>
                <svg 
                  className={`w-6 h-6 transition-colors duration-300 ${
                    isSearchFocused ? 'text-primary-500' : 'text-light-3'
                  }`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <Input
                  type="text"
                  placeholder="Search posts, hashtags, users..."
                  className="explore-search border-none bg-transparent text-light-1 placeholder:text-light-3 focus:ring-0 focus:border-none text-lg"
                  value={searchValue}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  onChange={(e) => {
                    const { value } = e.target;
                    setSearchValue(value);
                  }}
                />
                {searchValue && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={() => setSearchValue("")}
                    className="p-1 hover:bg-dark-2 rounded-full transition-colors"
                  >
                    <svg className="w-5 h-5 text-light-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </motion.button>
                )}
              </div>
              {isSearchFocused && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute -inset-1 bg-gradient-to-r from-primary-500/20 to-purple-500/20 rounded-2xl blur-sm -z-10"
                />
              )}
            </motion.div>

            {/* Search Stats */}
            {searchValue && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="text-center"
              >
                <p className="text-light-3">
                  Searching for: <span className="text-primary-500 font-semibold">"{searchValue}"</span>
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Results Section */}
        <motion.div 
          className="flex flex-wrap gap-9 w-full max-w-5xl justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <AnimatePresence mode="wait">
            {shouldShowSearchResults ? (
              <SearchResults
                isSearchFetching={isSearchFetching}
                searchedPosts={searchedPosts || { documents: [], total: 0 }}
              />
            ) : shouldShowPosts ? (
              <motion.div 
                key="end-of-posts"
                className="flex flex-col items-center justify-center min-h-[300px] text-center w-full"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="w-20 h-20 bg-dark-4 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-10 h-10 text-light-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-light-1 mb-2">No Posts Yet</h3>
                <p className="text-light-3">Be the first to create a post in our community!</p>
              </motion.div>
            ) : (
              <motion.div 
                key="posts-grid"
                className="w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {posts.pages.map((item, index) => (
                  <motion.div
                    key={`page-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <GridPostList posts={item.documents} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Load More Indicator */}
        {hasNextPage && !searchValue && (
          <motion.div 
            ref={ref} 
            className="mt-16 flex flex-col items-center gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="relative">
              <div className="w-12 h-12 border-4 border-primary-500/30 rounded-full animate-spin border-t-primary-500"></div>
            </div>
            <p className="text-light-3">Loading more amazing posts...</p>
          </motion.div>
        )}
      </motion.div>
    </>
  );
};

export default Explore;
